import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon } from '@mypoz/ui';

import { ToastContext } from './context.js';

const DISPLAY_MS = 3000;

/**
 * A single transient confirmation, anchored to the bottom of the viewport.
 *
 * MYP-52's first customer, Download invoice: the download itself happens out of sight
 * (`startDownload`, MYP-124), so this is what tells the customer their tap did something.
 * Built as a provider rather than a component OrderCard renders itself, because a toast has to be
 * positioned against the viewport, not against whichever card in a scrolled list triggered it.
 *
 * One message at a time, not a queue. Nothing in this app fires two of these close enough together
 * for a queue to matter yet, and getting a stack's ordering and lifetime right for a need that
 * doesn't exist yet is effort spent on the wrong thing. A second call while one is showing simply
 * replaces it and restarts the timer.
 *
 * Fixed rather than portalled, the same call Modal makes: one full-height layout root, nothing
 * clips a fixed child of it. Rendered after `children` so it paints above anything else fixed at
 * the same stacking level, Modal's backdrop included — z-[60] over Modal's z-50 makes that true
 * regardless of where in the tree either one happens to mount.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      if (timer.current !== undefined) window.clearTimeout(timer.current);
    },
    [],
  );

  const show = useCallback((next: string) => {
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    setMessage(next);
    timer.current = window.setTimeout(() => {
      timer.current = undefined;
      setMessage(null);
    }, DISPLAY_MS);
  }, []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}

      {message ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-5 z-[60] flex justify-center px-4">
          {/* aria-live rather than role="alert": this confirms a click, it doesn't demand attention
              the way an error would, so it should announce without interrupting whatever the
              screen reader is already reading. */}
          <div
            role="status"
            aria-live="polite"
            className="pointer-events-auto flex max-w-sm items-center gap-2 rounded-lg bg-ink px-4 py-3 text-sm font-medium text-white shadow-raised"
          >
            <Icon name="check_circle" size={18} className="shrink-0 text-success" fill />
            {message}
          </div>
        </div>
      ) : null}
    </ToastContext.Provider>
  );
}
